import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";

export type Tema = "white" | "black";

interface TemaContextValue {
  tema: Tema;
  setTema: (t: Tema) => void;
  alternarTema: () => void;
}

const TemaContext = createContext<TemaContextValue | undefined>(undefined);

function lerTemaInicial(): Tema {
  const attr = document.documentElement.getAttribute("data-theme");
  if (attr === "white" || attr === "black") return attr;
  return "black";
}

function aplicarTemaNoDom(tema: Tema) {
  document.documentElement.setAttribute("data-theme", tema);
  try {
    localStorage.setItem("tema", tema);
  } catch {
    /* ignora indisponibilidade de localStorage */
  }
}

/**
 * TemaProvider — controla o tema claro/escuro do sistema, no mesmo padrão do
 * legado (atributo data-theme="white" | "black" no elemento <html>) e persiste
 * a escolha em localStorage ("tema").
 */
export function TemaProvider({ children }: { children: ReactNode }) {
  const [tema, setTemaState] = useState<Tema>(lerTemaInicial);

  useEffect(() => {
    aplicarTemaNoDom(tema);
  }, [tema]);

  const setTema = useCallback((t: Tema) => {
    aplicarTemaNoDom(t);
    setTemaState(t);
  }, []);
  const alternarTema = useCallback(
    () => {
      const proximoTema = tema === "black" ? "white" : "black";
      aplicarTemaNoDom(proximoTema);
      setTemaState(proximoTema);
    },
    [tema]
  );

  return (
    <TemaContext.Provider value={{ tema, setTema, alternarTema }}>
      {children}
    </TemaContext.Provider>
  );
}

export function useTema(): TemaContextValue {
  const ctx = useContext(TemaContext);
  if (!ctx) throw new Error("useTema deve ser usado dentro de <TemaProvider>.");
  return ctx;
}
